/**
 * LRN/3DT戦略プランナー（gv-core の lrn.ts / three-dt-strategy.ts）の
 * 結果を文字列（HTML）にまとめるだけの、DOM非依存の純粋モジュール
 * （ui/apt-chart.ts と同じ流儀）。
 *
 * ★★ ここに出す数字は**飛行支援のための参考値**であって公式スコアでは
 *   ない。公式の採点は主催者の採点ソフトが行う。呼び出し側（index.html の
 *   #lrn3dt-view）で必ず常時表示の注記（.apt-disclaimer と同じ扱い）を
 *   添えること。
 *
 * 地図への描画（UTM⇄lat/lon変換）はここでは行わない。座標変換が絡む部分は
 * main.ts 側に置くという既存の役割分担（apt-chart.ts もチャートSVGの文字列
 * を返すだけで地図には触れない）を踏襲する。
 */

import type { LrnAdvice, LrnAdviceStep, LrnLeg, LrnPlan, ThreeDtPlan, ThreeDtSegment } from '@gv4d/core';
import { formatAccumulatedSec } from './apt-chart.ts';

function legLine(label: string, leg: LrnLeg): string {
  const durationSec = leg.toSec - leg.fromSec;
  return (
    `<li>${label}: 高度${Math.round(leg.layer.altFt)}ft・` +
    `風向${Math.round(leg.layer.dirToTrueDeg)}°・風速${leg.layer.speedMps.toFixed(1)}m/s に乗る` +
    `（${formatAccumulatedSec(leg.fromSec)}〜${formatAccumulatedSec(leg.toSec)}、` +
    `所要${formatAccumulatedSec(durationSec)}）</li>`
  );
}

/** `planLrnTime`/`planLrnCircle`の結果を、脚ごとの内訳＋面積のリストにする */
export function buildLrnPlanSummaryHtml(plan: LrnPlan): string {
  const [leg1, leg2] = plan.legs;
  const transitionSec = leg2.fromSec - leg1.toSec;
  const parts: string[] = ['<ul class="lrn3dt-summary">'];
  parts.push(legLine('第1脚', leg1));
  if (transitionSec > 0) {
    parts.push(
      `<li>高度移行: ${formatAccumulatedSec(leg1.toSec)}〜${formatAccumulatedSec(leg2.fromSec)}、` +
        `所要${formatAccumulatedSec(transitionSec)}</li>`,
    );
  }
  parts.push(legLine('第2脚', leg2));
  parts.push(`<li><b>推定面積: ${plan.areaKm2.toFixed(2)} km²</b></li>`);
  parts.push('</ul>');
  return parts.join('');
}

function legLineText(label: string, leg: LrnLeg): string {
  const durationSec = leg.toSec - leg.fromSec;
  return (
    `${label}: 高度${Math.round(leg.layer.altFt)}ft・` +
    `風向${Math.round(leg.layer.dirToTrueDeg)}°・風速${leg.layer.speedMps.toFixed(1)}m/s に乗る` +
    `（${formatAccumulatedSec(leg.fromSec)}〜${formatAccumulatedSec(leg.toSec)}、` +
    `所要${formatAccumulatedSec(durationSec)}）`
  );
}

/** buildLrnPlanSummaryHtmlのプレーンテキスト版（LINEなどでの共有用） */
export function buildLrnPlanSummaryText(plan: LrnPlan): string {
  const [leg1, leg2] = plan.legs;
  const transitionSec = leg2.fromSec - leg1.toSec;
  const lines: string[] = [legLineText('第1脚', leg1)];
  if (transitionSec > 0) {
    lines.push(
      `高度移行: ${formatAccumulatedSec(leg1.toSec)}〜${formatAccumulatedSec(leg2.fromSec)}、` +
        `所要${formatAccumulatedSec(transitionSec)}`,
    );
  }
  lines.push(legLineText('第2脚', leg2));
  lines.push(`推定面積: ${plan.areaKm2.toFixed(2)} km²`);
  return lines.join('\n');
}

function segmentLine(seg: ThreeDtSegment): string {
  const durationSec = seg.toSec - seg.fromSec;
  return (
    `<li>高度${Math.round(seg.altFt)}ft` +
    `（${formatAccumulatedSec(seg.fromSec)}〜${formatAccumulatedSec(seg.toSec)}、` +
    `所要${formatAccumulatedSec(durationSec)}）</li>`
  );
}

/** `planThreeDtRoute`の結果を、高度区間ごとの内訳＋リング内滞在距離のリストにする */
export function buildThreeDtPlanSummaryHtml(plan: ThreeDtPlan): string {
  if (plan.segments.length === 0) {
    return (
      '<p class="lrn3dt-empty">このプロファイルではリング内を通る経路が見つかりませんでした' +
      '（風プロファイルがリングの高度帯を全くカバーしていないか、高度移行だけで制限時間を' +
      '使い切っています）。</p>'
    );
  }
  const parts: string[] = ['<ul class="lrn3dt-summary">'];
  for (const seg of plan.segments) parts.push(segmentLine(seg));
  parts.push(`<li><b>リング内滞在距離の合計: ${Math.round(plan.distanceInsideM)} m</b></li>`);
  parts.push('</ul>');
  return parts.join('');
}

function segmentLineText(seg: ThreeDtSegment): string {
  const durationSec = seg.toSec - seg.fromSec;
  return (
    `高度${Math.round(seg.altFt)}ft` +
    `（${formatAccumulatedSec(seg.fromSec)}〜${formatAccumulatedSec(seg.toSec)}、` +
    `所要${formatAccumulatedSec(durationSec)}）`
  );
}

/** buildThreeDtPlanSummaryHtmlのプレーンテキスト版（LINEなどでの共有用） */
export function buildThreeDtPlanSummaryText(plan: ThreeDtPlan): string {
  if (plan.segments.length === 0) {
    return 'このプロファイルではリング内を通る経路が見つかりませんでした。';
  }
  const lines = plan.segments.map(segmentLineText);
  lines.push(`リング内滞在距離の合計: ${Math.round(plan.distanceInsideM)} m`);
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// LRN風層アドバイザー（docs/08 §67 の5件目、パイロット要望）
// ---------------------------------------------------------------------------

function stepLine(step: LrnAdviceStep): string {
  if (step.kind === 'mark') {
    return (
      `<li class="lrn-step-mark"><b>${formatAccumulatedSec(step.sec)}</b>: ` +
      `<b>${step.label}点をマーク</b>（高度${Math.round(step.altFt)}ft）</li>`
    );
  }
  if (step.kind === 'ride') {
    return (
      `<li><b>${formatAccumulatedSec(step.fromSec)}</b>: ` +
      `高度${Math.round(step.layer.altFt)}ftを維持` +
      `（風向${Math.round(step.layer.dirToTrueDeg)}°・${step.layer.speedMps.toFixed(1)}m/s、` +
      `${formatAccumulatedSec(step.toSec - step.fromSec)}）</li>`
    );
  }
  const verb = step.kind === 'climb' ? '上昇' : '降下';
  return (
    `<li class="lrn-step-move"><b>${formatAccumulatedSec(step.fromSec)}</b>: ` +
    `<b>${verb}開始</b> ${Math.round(step.fromAltFt)}ft → ${Math.round(step.toAltFt)}ft` +
    `（所要${formatAccumulatedSec(step.toSec - step.fromSec)}）</li>`
  );
}

/**
 * `adviseLrn` の結果を「行動の時系列＋候補のランキング」にする。
 *
 * ★ 候補の並べ替えは**推定面積**で行われている。併記する評点
 * （`lrnLayerPairScore`）は「大きな三角形を作りやすそうか」の粗い目安で、
 * 面積とは別物なので、順位と評点が逆転して見えることがある。
 * 誤解を招かないよう、表の見出しで両者が別物であることを示す。
 */
export function buildLrnAdviceHtml(advice: LrnAdvice): string {
  if (!advice.best) {
    const why =
      advice.usableLayerCount < 2
        ? `高度帯に入っている風の層が${advice.usableLayerCount}つしかありません（2つ以上必要です）。` +
          '高度帯を広げるか、風データを増やしてください。'
        : '制限時間内に収まる層の組み合わせがありませんでした。制限時間・上昇/降下速度を見直してください。';
    return `<p class="lrn3dt-empty">${why}</p>`;
  }

  const parts: string[] = [];

  parts.push('<h4 class="lrn-advice-head">飛行手順</h4>');
  parts.push('<ol class="lrn-advice-steps">');
  for (const step of advice.steps) parts.push(stepLine(step));
  parts.push('</ol>');

  if (advice.candidates.length > 1) {
    parts.push(
      '<h4 class="lrn-advice-head">層の組み合わせ候補' +
        '<span class="muted">（推定面積の大きい順。評点は目安で面積とは別物）</span></h4>',
    );
    // 列が多く、狭い画面でははみ出すので横スクロールできる箱に入れる
    parts.push('<div class="lrn-advice-table-wrap">');
    parts.push('<table class="lrn-advice-table"><thead><tr>');
    parts.push('<th>#</th><th>第1脚</th><th>第2脚</th><th>推定面積</th><th>移行</th><th>評点</th>');
    parts.push('</tr></thead><tbody>');
    advice.candidates.forEach((c, i) => {
      const a = `${Math.round(c.layerA.altFt)}ft ${Math.round(c.layerA.dirToTrueDeg)}°`;
      const b = `${Math.round(c.layerB.altFt)}ft ${Math.round(c.layerB.dirToTrueDeg)}°`;
      parts.push(
        `<tr${i === 0 ? ' class="lrn-advice-best"' : ''}>` +
          `<td>${i + 1}</td><td>${a}</td><td>${b}</td>` +
          `<td>${c.areaKm2.toFixed(2)} km²</td>` +
          `<td>${formatAccumulatedSec(c.transitionSec)}</td>` +
          `<td>${c.pairScore.toFixed(1)}</td></tr>`,
      );
    });
    parts.push('</tbody></table></div>');
  }

  return parts.join('');
}

function stepLineText(step: LrnAdviceStep): string {
  if (step.kind === 'mark') {
    return `${formatAccumulatedSec(step.sec)}: ${step.label}点をマーク（高度${Math.round(step.altFt)}ft）`;
  }
  if (step.kind === 'ride') {
    return (
      `${formatAccumulatedSec(step.fromSec)}: 高度${Math.round(step.layer.altFt)}ftを維持` +
      `（風向${Math.round(step.layer.dirToTrueDeg)}°・${step.layer.speedMps.toFixed(1)}m/s、` +
      `${formatAccumulatedSec(step.toSec - step.fromSec)}）`
    );
  }
  const verb = step.kind === 'climb' ? '上昇' : '降下';
  return (
    `${formatAccumulatedSec(step.fromSec)}: ${verb}開始 ${Math.round(step.fromAltFt)}ft → ${Math.round(step.toAltFt)}ft` +
    `（所要${formatAccumulatedSec(step.toSec - step.fromSec)}）`
  );
}

/**
 * buildLrnAdviceHtmlのプレーンテキスト版（LINEなどでの共有用）。
 * 「層の組み合わせ候補」の表は共有には向かない（列が多く文字だけでは読みにくい）
 * ため、飛行手順の時系列だけにする。
 */
export function buildLrnAdviceText(advice: LrnAdvice): string {
  if (!advice.best) return '';
  return advice.steps.map(stepLineText).join('\n');
}
